import React, { Dispatch, SetStateAction, useRef } from 'react';
import classes from './NavigationTabs.module.scss';
import { ContainerListItem } from '../../../components/actions/ListItem/ListItem.view';
import { ListItem } from '../../../components/actions/ListItem';
import { ListItemContainer } from '../../../components/containers/ListItemContainer';
import classNames from 'classnames';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '../../../components/images/Icon/Icon';
import { IconsEnum } from '../../../constants/icons';
import { OutsideClickWrapper } from '../../../components/actions/OutsideClickWrapper';
import { DropdownWrapper } from '../../../components/containers/DropdownWrapper';

export type NavigationTabsType = {
  label: string;
  url: string;
};

export type NavigationTabsViewProps = {
  tabItems: NavigationTabsType[];
  moreItems: NavigationTabsType[];
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

export const NavigationTabsView = React.forwardRef<HTMLUListElement, NavigationTabsViewProps>(
  (props, ref): JSX.Element => {
    const navigate = useNavigate();
    const location = useLocation();
    const portalWrapperRef = useRef<HTMLDivElement | null>(null);
    const moreRef = useRef<HTMLLIElement | null>(null);

    // we use clickSelect because we want navigation to be executed. down won't work since we moved elements to the portal
    // the only downside of this is if you have any touch button (e.g. IG grid slider) clicking on it, won't trigger the

    const onLinkClick = (url: string) => {
      if (url !== location.pathname) {
        navigate({ pathname: url });
      }
    };

    NavigationTabsView.displayName = 'NavigationTabs';
    const liClasses = classNames('cell shrink', classes.TabElement);

    const NavigationItems = props.tabItems.map((item, index) => {
      const navigationClasses = classNames(liClasses, {
        [classes.Selected]: location.pathname.startsWith(item.url)
      });

      return (
        <li key={`nav-item-${index}`} onClick={() => onLinkClick(item.url)} className={navigationClasses}>
          {item.label}
        </li>
      );
    });

    const MoreDropdownList: ContainerListItem[] = [];
    props.moreItems.forEach(item => {
      MoreDropdownList.push({
        action: () => {
          /**/
        },
        label: item.label || ''
      } as ContainerListItem);
    });

    let moreNavItemSelected = false;
    const moreNavigationItems = props.moreItems.map((item, index) => {
      moreNavItemSelected = (!moreNavItemSelected && location.pathname.startsWith(item.url)) || moreNavItemSelected;
      return (
        <ListItem
          key={`more-menu-item-${index}`}
          type={'li'}
          disabled={false}
          variation={'regular'}
          selected={location.pathname.startsWith(item.url)}
          action={() => {
            props.setOpen(false);
            onLinkClick(item.url);
          }}>
          {item.label}
        </ListItem>
      );
    });

    const moreLiClasses = classNames(liClasses, {
      [classes.Selected]: props.open || moreNavItemSelected,
      [classes.MoreNavigationTabItem]: true
    });

    const clickOutside = () => props.setOpen(false);

    return (
      <div className={'cell shrink'}>
        <OutsideClickWrapper
          onFocusOutside={clickOutside}
          onClickOutside={clickOutside}
          ref={ref}
          className={classNames(classes.NavigationTabs, 'grid-x')}
          component={'ul'}>
          {NavigationItems}
          {MoreDropdownList.length > 0 && (
            <li
              id={'more-navigation'}
              onClick={() => props.setOpen(!props.open)}
              className={moreLiClasses}
              ref={moreRef}>
              <div className={'cell grid-x align-justify'} ref={portalWrapperRef}>
                <div>More</div>
                <div className={classes.MoreDropDownIcon}>
                  <Icon name={IconsEnum.arrowDown} size={'medium'} />
                </div>
              </div>
              <DropdownWrapper expanded={props.open} portalWrapperRef={moreRef}>
                <ListItemContainer type={'ul'} expanded={props.open}>
                  {moreNavigationItems}
                </ListItemContainer>
              </DropdownWrapper>
            </li>
          )}
        </OutsideClickWrapper>
      </div>
    );
  }
);
